import { rotateLeft, range } from "client_legacy/ndaw/arrayHelpers";
import {
	DateTime,
	Duration,
	DurationUnit,
	WeekdayNumbers,
	WeekSettings
} from "luxon";
import { toDayOfWeek } from "./dateTimeUtils";

export function getDisplayFriendlyDelay(minutes: number): string {
	const duration = Duration.fromObject({ minutes })
		.shiftTo("minutes", "hours", "days", "weeks", "months", "years");

	const availableDurationUnits: readonly DurationUnit[] = [
		...(duration.minutes > 0 ? ["minutes"] as const : []),
		...(duration.hours > 0 ? ["hours"] as const : []),
		...(duration.days > 0 ? ["days"] as const : []),
		...(duration.weeks > 0 ? ["weeks"] as const : []),
		...(duration.months > 0 ? ["months"] as const : []),
		...(duration.years > 0 ? ["years"] as const : [])
	];

	return duration
		.shiftTo(...availableDurationUnits)
		.toHuman();
}

export enum DayOfWeek {
	Sunday = 0,
	Monday = 1,
	Tuesday = 2,
	Wednesday = 3,
	Thursday = 4,
	Friday = 5,
	Saturday = 6
}

export function getFirstWeekDay(): DayOfWeek {
	return toDayOfWeek(
		DateTime.now()
			.startOf("week", { useLocaleWeeks: true })
			.weekday
	);
}

export type DayOfWeekFormat = "short" | "full";

const dayOfWeekToLuxonFormat: {[key in DayOfWeekFormat]: string} = {
	"short": "ccc",
	"full": "cccc"
};

export function formatDayOfWeek(weekDay: DayOfWeek, format: DayOfWeekFormat): string {
	const formattedDay: string = DateTime.now()
		.set({ weekday: toLuxonWeekday(weekDay) })
		.toFormat(dayOfWeekToLuxonFormat[format]);

	return format === "short"
		? formattedDay.substring(0, 2)
		: formattedDay;
}

export function getShortMonthNames(): readonly string[] {
	const now: DateTime = DateTime.now();

	return range(1,13).map(
		m => now.set({ month: m })
			.toFormat("LLL")
	);
};

function toLuxonWeekday(dayOfWeek: DayOfWeek): WeekdayNumbers {
	if (dayOfWeek === DayOfWeek.Sunday) {
		return 7;
	}

	return dayOfWeek;
}

export function getWeekDays(firstWeekDay: DayOfWeek): readonly DayOfWeek[] {
	return rotateLeft(
		[
			DayOfWeek.Sunday,
			DayOfWeek.Monday,
			DayOfWeek.Tuesday,
			DayOfWeek.Wednesday,
			DayOfWeek.Thursday,
			DayOfWeek.Friday,
			DayOfWeek.Saturday
		],
		firstWeekDay
	);
}

export function getShortWeekDayNames(firstWeekDay: number): readonly string[] {
	return getWeekDays(firstWeekDay).map(
		x => formatDayOfWeek(x, "short")
	);
}

export const weekInfoPerCulture: Record<string, WeekSettings | undefined> = {
	da: {
		firstDay: 1,
		weekend: [6, 7],
		minimalDays: 4
	},
	en: {
		firstDay: 7,
		weekend: [6, 7],
		minimalDays: 1
	},
	de: {
		firstDay: 1,
		weekend: [6, 7],
		minimalDays: 4
	},
	it: {
		firstDay: 1,
		weekend: [6, 7],
		minimalDays: 4
	},
	nb: {
		firstDay: 1,
		weekend: [6, 7],
		minimalDays: 4
	},
	nl: {
		firstDay: 1,
		weekend: [6, 7],
		minimalDays: 4
	},
	sv: {
		firstDay: 1,
		weekend: [6, 7],
		minimalDays: 4
	},
	es: {
		firstDay: 1,
		weekend: [6, 7],
		minimalDays: 4
	},
	"en-AU": {
		firstDay: 1,
		weekend: [6, 7],
		minimalDays: 1
	},
	"en-GB": {
		firstDay: 1,
		weekend: [6, 7],
		minimalDays: 4
	},
	"en-NZ": {
		firstDay: 1,
		weekend: [6, 7],
		minimalDays: 1
	},
	fr: {
		firstDay: 1,
		weekend: [6, 7],
		minimalDays: 4
	}
};
